import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 16, fontWeight: 700, color: '#00F0FF', marginBottom: 10, fontFamily: 'var(--font-heading)' }}>{title}</h2>
    <div style={{ fontSize: 14, color: '#B0B8D0', lineHeight: 1.8 }}>{children}</div>
  </div>
);

const PrivacyPage = () => {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#020210', padding: '0 0 60px' }}>
      {/* Navbar */}
      <nav style={{
        background: 'rgba(2,2,16,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(112,0,255,0.3)',
        padding: '0 24px', height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <Shield size={22} color="#00F0FF" />
          <span style={{ fontWeight: 800, fontSize: 18, color: '#fff' }}>Web<span style={{ color: '#00F0FF' }}>Shield</span></span>
        </div>
        <button onClick={() => navigate(-1)} style={{
          background: 'transparent', border: '1px solid #2a2a4a', borderRadius: 8,
          padding: '6px 14px', color: '#b0b8d0', fontSize: 13, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <ArrowLeft size={14} /> Back
        </button>
      </nav>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 6, fontFamily: 'var(--font-heading)' }}>
          Privacy Policy
        </h1>
        <p style={{ color: '#6a7a9a', fontSize: 13, marginBottom: 36 }}>Last updated: April 2026</p>

        <Section title="1. Who we are">
          WebShield ("we", "our", "us") operates the website security scanning service at <span style={{ color: '#00F0FF' }}>webshield.app</span>. This policy explains what data we collect, why we collect it, and how it is used.
        </Section>

        <Section title="2. What data we collect">
          <p style={{ marginBottom: 8 }}>When you use WebShield, we collect:</p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Website URL</strong> — the URL you submit for scanning.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Email address</strong> — used to deliver your PDF security report.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Payment information</strong> — processed entirely by Razorpay. We never see or store your card details.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Scan results</strong> — stored temporarily to generate your PDF and allow report re-delivery.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Usage data</strong> — anonymised page views and interaction events via PostHog analytics.</li>
          </ul>
        </Section>

        <Section title="3. How we use your data">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li style={{ marginBottom: 6 }}>To run the security scan on the URL you submitted.</li>
            <li style={{ marginBottom: 6 }}>To generate and email your PDF security report.</li>
            <li style={{ marginBottom: 6 }}>To process your payment via Razorpay.</li>
            <li style={{ marginBottom: 6 }}>To improve the service using aggregated, anonymised analytics.</li>
          </ul>
          <p style={{ marginTop: 10 }}>We do <strong style={{ color: '#fff' }}>not</strong> sell, rent, or share your personal data with third parties for marketing purposes.</p>
        </Section>

        <Section title="4. Data retention">
          Scan records (URL, email, results) are retained for up to <strong style={{ color: '#fff' }}>30 days</strong> to allow report re-delivery, then permanently deleted. Payment records are retained by Razorpay under their own policies.
        </Section>

        <Section title="5. Third-party services">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Razorpay</strong> — payment processing. Subject to <a href="https://razorpay.com/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: '#00F0FF' }}>Razorpay's Privacy Policy</a>.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>PostHog</strong> — anonymous usage analytics. No personally identifiable data is sent.</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>MongoDB Atlas</strong> — encrypted database hosting (AWS Mumbai region).</li>
            <li style={{ marginBottom: 6 }}><strong style={{ color: '#fff' }}>Render</strong> — backend hosting provider.</li>
          </ul>
        </Section>

        <Section title="6. Cookies">
          We use a minimal session cookie for analytics (PostHog). No advertising or tracking cookies are used. You can disable cookies in your browser settings at any time. See our cookie consent notice on your first visit.
        </Section>

        <Section title="7. Your rights">
          You have the right to request deletion of your data at any time. Email us at <a href="mailto:webshieldapp@gmail.com" style={{ color: '#00F0FF' }}>webshieldapp@gmail.com</a> and we will delete your scan records within 72 hours.
        </Section>

        <Section title="8. Security">
          All data is transmitted over HTTPS. Database access is restricted to our application servers. Payment data never passes through our systems.
        </Section>

        <Section title="9. Contact">
          For any privacy-related questions, contact us at <a href="mailto:webshieldapp@gmail.com" style={{ color: '#00F0FF' }}>webshieldapp@gmail.com</a>.
        </Section>
      </div>
    </div>
  );
};

export default PrivacyPage;